import { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiasApi from 'config/api/dpias/dpias';
import type { DpiaDetail, DpiaRisk } from 'config/api/dpias/dpias.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';

import { mapDetailToFormValues, mapFormValuesToUpdateInput, type OwnerDpiaFormValues } from './ownerDpiaEditor.helpers';

type Params = {
  dpiaId: number;
  onConflict?: () => void;
  onNavigate: () => void;
  onSaveSuccess?: (closeAfterSave: boolean) => void;
  onSubmitSuccess?: () => void;
  organizationId?: number;
};

const isTemporaryId = (id: number) => id < 0;

const syncNestedRisksAndMeasures = async (
  organizationId: number,
  dpiaId: number,
  oldRisks: DpiaRisk[],
  newRisks: DpiaRisk[],
): Promise<void> => {
  const risksToDelete = oldRisks.filter(oldRisk => !newRisks.some(newRisk => newRisk.id === oldRisk.id));
  await Promise.all(risksToDelete.map(risk => dpiasApi.deleteRisk(organizationId, dpiaId, risk.id)));

  // Sequential creates so temporary measure ids can reference the new risk id.
  // eslint-disable-next-line no-restricted-syntax
  for (const newRisk of newRisks) {
    const isNewRisk = isTemporaryId(newRisk.id);
    let realRiskId = newRisk.id;

    if (isNewRisk) {
      // eslint-disable-next-line no-await-in-loop
      const { data: createdRisk } = await dpiasApi.createRisk(organizationId, dpiaId, {
        title: newRisk.title,
        description: newRisk.description,
        affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
        likelihood: newRisk.likelihood,
        impact: newRisk.impact,
        sortOrder: newRisk.sortOrder,
      });
      realRiskId = createdRisk.id;
    } else {
      const oldRisk = oldRisks.find(risk => risk.id === newRisk.id);
      const needsUpdate =
        !oldRisk ||
        oldRisk.title !== newRisk.title ||
        oldRisk.description !== newRisk.description ||
        oldRisk.affectedRightsAndFreedoms !== newRisk.affectedRightsAndFreedoms ||
        oldRisk.likelihood !== newRisk.likelihood ||
        oldRisk.impact !== newRisk.impact ||
        oldRisk.sortOrder !== newRisk.sortOrder;

      if (needsUpdate) {
        // eslint-disable-next-line no-await-in-loop
        await dpiasApi.updateRisk(organizationId, dpiaId, realRiskId, {
          title: newRisk.title,
          description: newRisk.description,
          affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          sortOrder: newRisk.sortOrder,
        });
      }
    }

    const oldMeasures = isNewRisk ? [] : oldRisks.find(risk => risk.id === newRisk.id)?.measures || [];
    const newMeasures = newRisk.measures;

    const measuresToDelete = oldMeasures.filter(oldMeasure => !newMeasures.some(newMeasure => newMeasure.id === oldMeasure.id));
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(measuresToDelete.map(measure => dpiasApi.deleteMeasure(organizationId, dpiaId, measure.id)));

    // eslint-disable-next-line no-restricted-syntax
    for (const newMeasure of newMeasures) {
      if (isTemporaryId(newMeasure.id)) {
        // eslint-disable-next-line no-await-in-loop
        await dpiasApi.createMeasure(organizationId, dpiaId, {
          dpiaRiskId: realRiskId,
          title: newMeasure.title,
          description: newMeasure.description,
          responsibleMembershipId: newMeasure.responsibleMembershipId,
          dueDate: newMeasure.dueDate,
          status: newMeasure.status,
          sortOrder: newMeasure.sortOrder,
        });
      } else {
        const oldMeasure = oldMeasures.find(measure => measure.id === newMeasure.id);
        const needsUpdate =
          !oldMeasure ||
          oldMeasure.title !== newMeasure.title ||
          oldMeasure.description !== newMeasure.description ||
          oldMeasure.responsibleMembershipId !== newMeasure.responsibleMembershipId ||
          oldMeasure.dueDate !== newMeasure.dueDate ||
          oldMeasure.status !== newMeasure.status ||
          oldMeasure.sortOrder !== newMeasure.sortOrder;

        if (needsUpdate) {
          // eslint-disable-next-line no-await-in-loop
          await dpiasApi.updateMeasure(organizationId, dpiaId, newMeasure.id, {
            title: newMeasure.title,
            description: newMeasure.description,
            responsibleMembershipId: newMeasure.responsibleMembershipId,
            dueDate: newMeasure.dueDate,
            status: newMeasure.status,
            sortOrder: newMeasure.sortOrder,
          });
        }
      }
    }
  }
};

const persistDpiaDraft = async (organizationId: number, dpiaId: number, currentDetail: DpiaDetail, formValues: OwnerDpiaFormValues) => {
  await syncNestedRisksAndMeasures(organizationId, dpiaId, currentDetail.risks, formValues.risks);

  const { data } = await dpiasApi.update(organizationId, dpiaId, mapFormValuesToUpdateInput(currentDetail, formValues));

  try {
    await dpiasApi.updatePurposeScope(organizationId, dpiaId, {
      selectedPurposeIds: formValues.selectedPurposeIds,
    });
  } catch {
    // Best-effort purpose-scope sync when the nested endpoint is unavailable.
  }

  return data;
};

const useOwnerDpiaEditor = ({ dpiaId, onConflict, onNavigate, onSaveSuccess, onSubmitSuccess, organizationId }: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<OwnerDpiaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId],
    async () => {
      const { data } = await dpiasApi.getDetail(organizationId as number, dpiaId);
      return data;
    },
    {
      enabled: Boolean(organizationId && dpiaId),
      retry: false,
    },
  );

  useEffect(() => {
    if (detailQuery.data?.dpia) {
      setFormValues(mapDetailToFormValues(detailQuery.data.dpia));
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [detailQuery.data?.dpia]);

  const handleConflictError = useCallback(
    async (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await queryClient.invalidateQueries([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId]);
        onConflict?.();
      }
    },
    [dpiaId, onConflict, organizationId, queryClient],
  );

  const saveMutation = useMutation(
    MUTATION_KEYS.DPIA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (!formValues || !detailQuery.data?.dpia || !organizationId) return null;

      const data = await persistDpiaDraft(organizationId, dpiaId, detailQuery.data.dpia, formValues);
      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], result.data);
        setFormValues(mapDetailToFormValues(result.data.dpia));
        setIsDirty(false);
        onSaveSuccess?.(result.closeAfterSave);
      },
      onError: (error: unknown) => {
        handleConflictError(error);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.DPIA_SUBMIT,
    async () => {
      if (!detailQuery.data?.dpia || !formValues || !organizationId) return null;

      let currentDetail = detailQuery.data.dpia;

      if (isDirty) {
        const data = await persistDpiaDraft(organizationId, dpiaId, currentDetail, formValues);
        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        currentDetail = data.dpia;
      }

      const { data } = await dpiasApi.submit(organizationId, dpiaId, { revision: currentDetail.revision });
      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        setFormValues(mapDetailToFormValues(data.dpia));
        setIsDirty(false);
        onSubmitSuccess?.();
      },
      onError: (error: unknown) => {
        handleConflictError(error);
      },
    },
  );

  const patchFormValues = useCallback((patch: Partial<OwnerDpiaFormValues>) => {
    setFormValues(current => (current ? { ...current, ...patch } : current));
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleSaveClick = useCallback(
    async (closeAfterSave: boolean) => {
      await saveMutation.mutateAsync(closeAfterSave);
    },
    [saveMutation],
  );

  const handleSubmit = useCallback(async () => {
    await submitMutation.mutateAsync();
  }, [submitMutation]);

  const { NavigationPrompt, handleNavigation } = useUnsavedChangesProtection({
    enabled: Boolean(detailQuery.data?.dpia.canEdit),
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  return useMemo(
    () => ({
      context: detailQuery.data?.context,
      detail: detailQuery.data?.dpia,
      formValues,
      handleNavigation,
      handleSave: handleSaveClick,
      handleSubmit,
      isDirty,
      isError: detailQuery.isError,
      isLoading: detailQuery.isLoading,
      isSaving: saveMutation.isLoading,
      isSubmitting: submitMutation.isLoading,
      NavigationPrompt,
      patchFormValues,
      refetch: detailQuery.refetch,
      saveMutation,
      setFormValues,
      setIsDirty,
      setValidationErrors,
      submitMutation,
      validationErrors,
    }),
    [
      detailQuery.data,
      detailQuery.isError,
      detailQuery.isLoading,
      detailQuery.refetch,
      formValues,
      handleNavigation,
      handleSaveClick,
      handleSubmit,
      isDirty,
      NavigationPrompt,
      patchFormValues,
      saveMutation,
      submitMutation,
      validationErrors,
    ],
  );
};

export default useOwnerDpiaEditor;
